import { ExpandMore } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Collapse, ListItemButton, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import React, { useContext, useId, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SidebarContext from '../context/SidebarContext';
import classes from '../styles/sideBarStyles.module.css';
import SearchBox from '../core-components/theme/components/Searchbox/Searchbox';
import { Button } from '../core-components/theme/components/Buttons';
import { LoadingState } from '../core-components/theme/components/Screen/ScreenStates';
import { useSidebarState } from '../access';
import type { SidebarItem, SidebarState } from '../access';
import { useUnifiedDashboardStore } from '../store/UnifiedDashboardStore';
import { trackEvent } from '../modules/analytics/trackingMiddleware';
import { SIDEBAR_NAV_CLICKED } from '../modules/analytics/events';

interface PropsInterface {
  sideBarMenu?: SidebarItem[];
  refresh?: Function;
  close?: Function;
}

const NO_ITEMS: readonly SidebarItem[] = [];

const filterMenu = (menuItems: readonly SidebarItem[], searchString: string): SidebarItem[] => {
  const lowerSearch = searchString.toLowerCase();
  return menuItems.flatMap((item): SidebarItem[] => {
    const filtered: SidebarItem = item.children ? { ...item, children: filterMenu(item.children, searchString) } : { ...item };
    const nameMatches = item.name.toLowerCase().includes(lowerSearch);
    return nameMatches || (filtered.children && filtered.children.length > 0) ? [filtered] : [];
  });
};

interface MenuGroupProps {
  readonly item: SidebarItem;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly children: React.ReactNode;
}

const MenuGroup = ({ item, open, onToggle, children }: MenuGroupProps) => {
  const groupId = useId();
  return (
    <>
      <ListItemButton className={classes.menuItem} aria-expanded={open} aria-controls={open ? groupId : undefined} onClick={onToggle}>
        <ListItemText primary={item.name} className={classes.icon} />
        {open ? <ExpandMore className={classes.icon} /> : <ChevronRightIcon className={classes.icon} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List id={groupId} component="div" disablePadding sx={{ pl: 4 }}>
          {children}
        </List>
      </Collapse>
    </>
  );
};

interface SidebarBodyProps {
  readonly sidebar: SidebarState;
  readonly renderItems: () => React.ReactNode;
}

const SidebarBody = ({ sidebar, renderItems }: SidebarBodyProps) => {
  const { t } = useTranslation();
  switch (sidebar.status) {
    case 'ready':
      return <List>{renderItems()}</List>;
    case 'loading':
      return <LoadingState label={t('sidebar.loading', 'Loading the menu')} />;
    case 'failed':
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <Typography variant="body">{t('sidebar.loadFailed', 'Couldn’t load the menu.')}</Typography>
          <Button intent="secondary" onClick={sidebar.retry}>
            {t('common.tryAgain', 'Try again')}
          </Button>
        </Box>
      );
    case 'absent':
      return null;
    default: {
      const unhandled: never = sidebar;
      throw new Error(`Sidebar state without a body: ${JSON.stringify(unhandled)}`);
    }
  }
};

export default function MiniDrawer(props: PropsInterface) {
  const { t } = useTranslation();
  const { open, setOpen } = useContext(SidebarContext);
  const [searchString, setSearchString] = useState<string>('');
  const [menuItemState, setMenuItemState] = useState<{
    [key: string]: boolean;
  }>({});

  const { refresh, close } = props;
  const sidebar = useSidebarState();
  const sideBarMenu = sidebar.status === 'ready' ? sidebar.items : NO_ITEMS;

  const filteredMenu = useMemo(() => filterMenu(sideBarMenu, searchString), [sideBarMenu, searchString]);

  const handleClick = (name: string) => {
    setMenuItemState(prevState => ({
      ...prevState,
      [name]: !prevState[name],
    }));
    refresh?.();
  };

  const handleOnClose = () => {
    setSearchString('');
    setOpen(false);
  };

  const onNavigate = (item: SidebarItem) => {
    useUnifiedDashboardStore.getState().resetUnifiedDashboardStore();
    trackEvent(SIDEBAR_NAV_CLICKED, { route: item.link ?? '', name: item.name });
    close?.();
    setOpen(false);
    setSearchString('');
    setMenuItemState({});
  };

  const menuItem = (item: SidebarItem): React.ReactNode => {
    if (item.children) {
      return (
        <MenuGroup item={item} open={Boolean(menuItemState[item.name])} onToggle={() => handleClick(item.name)}>
          {item.children.map(child => (
            <React.Fragment key={child.link ?? child.name}>{menuItem(child)}</React.Fragment>
          ))}
        </MenuGroup>
      );
    }
    return (
      <ListItemButton component={RouterLink} to={item.link ?? ''} className={classes.menuItem} onClick={() => onNavigate(item)}>
        <ListItemText primary={item.name} className={classes.icon} />
      </ListItemButton>
    );
  };

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={() => handleOnClose()}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.toolbar}>
        <SearchBox
          onChange={e => setSearchString(e.target.value)}
          onClear={() => setSearchString('')}
          borderRadius="40px"
          size="small"
          placeholder={t('sidebar.searchPlaceholder', 'Search ...')}
        />
      </div>
      <Divider />
      <SidebarBody
        sidebar={sidebar}
        renderItems={() =>
          filteredMenu.map(item => <React.Fragment key={item.link ?? item.name}>{menuItem(item)}</React.Fragment>)
        }
      />
    </Drawer>
  );
}
